import { useCallback, useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from '@tanstack/react-router';
import { Route } from '@/routes/_authenticated/setup/assignment-rules';
import { Pencil, Plus, User, Bot, Users } from 'lucide-react';
import type { TaskAssignmentRuleResponse } from '@vagusx/clients';
import { TablePage, SortableHeader } from '@/components/table-page';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAssignmentRules } from '@/hooks/use-assignment-rules';
import { CatchAllCard } from './CatchAllCard';
import { RuleEditor } from './RuleEditor';
import { RuleSummary } from './RuleSummary';

const TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  user: User,
  agent: Bot,
  team: Users,
};

function AssignToCell({ rule }: { rule: TaskAssignmentRuleResponse }): React.ReactElement {
  const Icon = TYPE_ICONS[rule.assignToType] ?? User;
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <Badge variant="outline" className="text-xs capitalize">
        {rule.assignToType}
      </Badge>
      {rule.assignToName && <span className="truncate text-sm">{rule.assignToName}</span>}
    </div>
  );
}

/**
 * Columns depend on `onEdit`, so they're built per-render rather than
 * held as a module const. The trailing actions column is a pencil that
 * only materialises on row hover (or keyboard focus) — the whole row is
 * already clickable, so the icon is an affordance, not the only way in.
 */
function buildColumns(
  onEdit: (rule: TaskAssignmentRuleResponse) => void
): ColumnDef<TaskAssignmentRuleResponse>[] {
  return [
    {
      accessorKey: 'name',
      header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
      cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
    },
    {
      accessorKey: 'conditions',
      header: 'Conditions',
      cell: ({ row }) => <RuleSummary conditions={row.original.conditions} />,
    },
    {
      accessorKey: 'assignToType',
      header: 'Assign To',
      cell: ({ row }) => <AssignToCell rule={row.original} />,
    },
    {
      id: 'actions',
      header: '',
      meta: { headerClassName: 'w-12', cellClassName: 'text-right' },
      cell: ({ row }) => (
        <Button
          size="icon-sm"
          variant="ghost"
          aria-label={`Edit ${row.original.name}`}
          title="Edit rule"
          // No `transition-opacity`: Button's base `transition-all` already covers
          // opacity, and tailwind-merge would drop it in favour of the narrower class.
          // Touch screens can't hover, so they keep it visible (as the other setup tables do).
          className="opacity-0 focus-visible:opacity-100 group-hover:opacity-100 [@media(any-pointer:coarse)]:opacity-100"
          onClick={(e) => {
            // The row's own click handler also opens the editor; stop
            // the bubble so it isn't triggered twice.
            e.stopPropagation();
            onEdit(row.original);
          }}
        >
          <Pencil className="h-4 w-4" />
        </Button>
      ),
    },
  ];
}

export default function AssignmentRulesPage(): React.ReactElement {
  const { id: selectedRuleId, mode } = Route.useSearch();
  const navigate = useNavigate();

  const { data, isLoading, error } = useAssignmentRules();
  const rules = data?.items ?? [];

  const isEditing = !!selectedRuleId || mode === 'create' || mode === 'create-catch-all';

  const handleRuleSelect = useCallback(
    (rule: TaskAssignmentRuleResponse) => {
      navigate({ to: '/setup/assignment-rules', search: { id: rule.id }, replace: true });
    },
    [navigate]
  );

  const columns = useMemo(() => buildColumns(handleRuleSelect), [handleRuleSelect]);

  const handleClose = () => {
    navigate({ to: '/setup/assignment-rules', search: {}, replace: true });
  };

  // Full-width inline editor mode. Three create paths share the same
  // editor: existing rule (id), new regular rule (mode=create), new
  // catch-all (mode=create-catch-all). The editor branches on the
  // initial isCatchAll flag.
  if (isEditing) {
    return (
      <RuleEditor
        ruleId={mode === 'create' || mode === 'create-catch-all' ? undefined : selectedRuleId}
        initialIsCatchAll={mode === 'create-catch-all'}
        onClose={handleClose}
      />
    );
  }

  // List mode
  return (
    <TablePage
      title="Assignment Rules"
      description="Automatically assign tasks based on conditions"
      columns={columns}
      data={rules}
      total={data?.total}
      isLoading={isLoading}
      error={error}
      searchColumn="name"
      searchPlaceholder="Search rules..."
      onRowClick={handleRuleSelect}
      emptyMessage="No assignment rules yet."
      errorMessage="Failed to load assignment rules."
      aboveTable={
        <CatchAllCard
          onCreate={() =>
            navigate({
              to: '/setup/assignment-rules',
              search: { mode: 'create-catch-all' },
              replace: true,
            })
          }
          onEdit={(rule) =>
            navigate({
              to: '/setup/assignment-rules',
              search: { id: rule.id },
              replace: true,
            })
          }
        />
      }
      headerAction={
        <Button
          onClick={() =>
            navigate({ to: '/setup/assignment-rules', search: { mode: 'create' }, replace: true })
          }
        >
          <Plus className="mr-2 h-4 w-4" />
          Create Rule
        </Button>
      }
    />
  );
}
